import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer, LabelList,
} from "recharts";
import { ArrowLeft, Trophy } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoricalResultsPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "#334155" : "#000000";

  useEffect(() => {
    axios
      .get(`${API}/historical-results`)
      .then((r) => setResults(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const active = results[activeIdx];

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="history-page">
      {/* Header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <button
            data-testid="history-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Historical Results
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Past Election Results
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            Browse seat counts and primary vote percentages from recent Australian Federal and State elections.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {loading ? (
          <div className="h-64 border-2 border-black animate-pulse" style={{ backgroundColor: surface }} />
        ) : (
          <>
            {/* Election selector */}
            <div className="flex flex-wrap gap-2 mb-8">
              {results.map((r, idx) => (
                <button
                  key={r.id}
                  data-testid={`history-election-${r.id}`}
                  onClick={() => setActiveIdx(idx)}
                  className="px-4 py-2 border-2 font-bold text-sm transition-all hover:-translate-y-0.5"
                  style={{
                    borderColor: idx === activeIdx ? "#000" : borderC,
                    backgroundColor: idx === activeIdx ? "#FFC82E" : surface,
                    color: idx === activeIdx ? "#000" : text,
                    boxShadow: idx === activeIdx ? "2px 2px 0px 0px #000" : "none",
                  }}
                >
                  {r.name}
                </button>
              ))}
            </div>

            {active && (
              <div className="space-y-6">
                {/* Winner banner */}
                <div
                  className="border-2 border-black p-5 flex items-center gap-4"
                  style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
                >
                  <Trophy className="w-8 h-8 flex-shrink-0" style={{ color: "#FFC82E" }} />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: textSec }}>
                      Winner — {active.date}
                    </p>
                    <h2
                      className="text-2xl font-black"
                      style={{ fontFamily: "Outfit, sans-serif", color: text }}
                    >
                      {active.winner}
                    </h2>
                    {active.two_pp && (
                      <p className="text-sm" style={{ color: textSec }}>
                        Two-party preferred: ALP {active.two_pp.alp}% | Coalition {active.two_pp.coalition}%
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Seats chart */}
                  <div
                    className="border-2 border-black p-5"
                    style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
                  >
                    <h3 className="font-black mb-4" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
                      Seats Won
                      {active.seats_total && (
                        <span className="text-sm font-semibold ml-2" style={{ color: textSec }}>
                          (of {active.seats_total})
                        </span>
                      )}
                    </h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={active.results} layout="vertical" margin={{ left: 10, right: 30 }}>
                        <XAxis type="number" tick={{ fontSize: 11, fill: textSec }} />
                        <YAxis type="category" dataKey="party" tick={{ fontSize: 11, fill: text }} width={80} />
                        <Tooltip
                          formatter={(v) => [v, "Seats"]}
                          contentStyle={{ backgroundColor: surface, border: "2px solid #000", borderRadius: 0 }}
                        />
                        <Bar dataKey="seats" radius={0}>
                          <LabelList dataKey="seats" position="right" style={{ fontSize: 11, fill: text }} />
                          {active.results.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Primary vote chart */}
                  <div
                    className="border-2 border-black p-5"
                    style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
                  >
                    <h3 className="font-black mb-4" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
                      Primary Vote %
                    </h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={active.results} margin={{ right: 20 }}>
                        <XAxis dataKey="party" tick={{ fontSize: 10, fill: text }} />
                        <YAxis domain={[0, 55]} tick={{ fontSize: 11, fill: textSec }} unit="%" />
                        <Tooltip
                          formatter={(v) => [`${v}%`, "Primary Vote"]}
                          contentStyle={{ backgroundColor: surface, border: "2px solid #000", borderRadius: 0 }}
                        />
                        <Bar dataKey="primary_vote" radius={0}>
                          <LabelList
                            dataKey="primary_vote"
                            position="top"
                            formatter={(v) => `${v}%`}
                            style={{ fontSize: 10, fill: text }}
                          />
                          {active.results.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Results table */}
                <div
                  className="border-2 border-black overflow-x-auto"
                  style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
                >
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: isDark ? "#0F172A" : "#F9F7F2" }}>
                        <th className="p-3 text-left text-xs font-black border-b-2 border-black" style={{ color: textSec }}>Party</th>
                        {active.results[0]?.seats !== undefined && (
                          <th className="p-3 text-right text-xs font-black border-b-2 border-black" style={{ color: textSec }}>Seats</th>
                        )}
                        <th className="p-3 text-right text-xs font-black border-b-2 border-black" style={{ color: textSec }}>Primary Vote</th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.results.map((r, i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 border border-black flex-shrink-0" style={{ backgroundColor: r.color }} />
                              <span className="text-sm font-semibold" style={{ color: text }}>{r.party}</span>
                            </div>
                          </td>
                          {r.seats !== undefined && (
                            <td className="p-3 text-right font-black" style={{ color: text }}>{r.seats}</td>
                          )}
                          <td className="p-3 text-right font-bold" style={{ color: text }}>{r.primary_vote}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
